/**
 * useTaskData.ts
 * -----------------------------------------------------------------------------
 * Hook for fetching and managing task data from the unified tasks API.
 * Supports both workflow and check-in tasks with filtering and sorting.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { Task, TaskListSettings } from './TaskListMolecule.types';
import { generateVirtualTasksFromSchedules, FactorScheduleInfo } from './scheduleResolver';
import { readTaskPages } from './readTaskPages';
import { isTransientReadFailure } from '../../../utils/isTransientReadFailure';
import type { GlyphColor } from '../../../types/glyph';

/**
 * Counts returned by the unified tasks API.
 *
 * `behind`, `upcoming`, `completed30d`, and `completed30dPrev` are shipped by
 * graph-service Phase 3.2 (see spec §task-list-redesign). They are optional so
 * this hook degrades gracefully against older backends; consumers should treat
 * `undefined` as "not yet available" (e.g. render "—" in the 30d trend card)
 * rather than 0.
 */
export interface UnifiedTasksCounts {
  total: number;
  workflow: number;
  checkIn: number;
  checkInVirtual?: number;
  pending?: number;
  overdue: number;
  behind?: number;
  upcoming?: number;
  completed30d?: number;
  completed30dPrev?: number;
}

interface UnifiedTasksResponse {
  success: boolean;
  tasks: Task[];
  counts: UnifiedTasksCounts;
  pagination: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  /** Factor schedules for client-side virtual task generation */
  factorSchedules?: FactorScheduleInfo[];
  error?: string;
}

/** Context resolution metadata returned by the context tasks endpoint */
export interface ContextMeta {
  resolvedSphereCount: number;
  activeSphereCount: number;
  cacheHit: boolean;
}

/** Sphere-grouped task bucket from the context tasks endpoint */
export interface TaskGroup {
  sphereId: string;
  sphereName: string;
  sphereType?: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
  taskCount: number;
  tasks: Task[];
}

/** Extended response shape (superset of both endpoints — context fields optional) */
interface ContextTasksResponse extends UnifiedTasksResponse {
  contextMeta?: ContextMeta;
  groups?: TaskGroup[];
}

interface UseTaskDataResult {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  counts: UnifiedTasksCounts | null;
  hasMore: boolean;
  refetch: () => void;
  refresh: () => Promise<void>;
  /** Re-reads after a sphere in the list changed: no loading state, and a failure keeps the rows. */
  refreshQuietly: () => Promise<void>;
  fetchMore: () => Promise<void>;
  /** Context resolution metadata (only present in context mode) */
  contextMeta: ContextMeta | null;
  /** Sphere-grouped tasks (only present when groupBySphere is enabled in context mode) */
  groups: TaskGroup[] | null;
}

export const useTaskData = (
  sphereId: string,
  settings?: TaskListSettings,
  contextId?: string | null
): UseTaskDataResult => {
  const apiClients = useApiClientsOrNull();
  const [materializedTasks, setMaterializedTasks] = useState<Task[]>([]);
  const [factorSchedules, setFactorSchedules] = useState<FactorScheduleInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<UnifiedTasksCounts | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [contextMeta, setContextMeta] = useState<ContextMeta | null>(null);
  const [groups, setGroups] = useState<TaskGroup[] | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();
  // Whether the rows on screen came from a read that succeeded. A normal reset read clears it: the
  // rows it replaces no longer answer what was asked for.
  const hasLoadedRef = useRef(false);
  const limit = 50; // Fetch materialized tasks in reasonable batches

  // `quiet` re-reads in the background: no loading state, and a transient failure (network, cancel, 429,
  // 5xx) keeps the rows on screen; a refusal (403, 404, anything else) takes the normal failure path and
  // clears them (D71). It re-reads every page already loaded, and moves the offset only when the read succeeds. With
  // nothing loaded yet there are no rows to keep, so a quiet read behaves as a normal one: it may
  // have cancelled the first load, and its failure has to show the error state, not an empty list.
  const fetchTasks = useCallback(async (reset = true, quietRequested = false) => {
    if (!apiClients || !sphereId) {
      return;
    }
    const quiet = quietRequested && hasLoadedRef.current;

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (reset && !quiet) {
      setLoading(true);
      hasLoadedRef.current = false;
    }
    if (!quiet) setError(null);

    const buildEndpoint = (pageOffset: number): string => {
      const params = new URLSearchParams();
      params.set('limit', limit.toString());
      params.set('offset', pageOffset.toString());

      // Task type filter
      if (settings?.filterByTaskType && settings.filterByTaskType !== 'all') {
        params.set('taskType', settings.filterByTaskType);
      }

      // Arc stage filter (for workflow tasks)
      if (settings?.filterByStatus && settings.filterByStatus.length > 0) {
        params.set('arcStage', settings.filterByStatus[0]);
      }

      // Check-in status filter
      if (settings?.filterByCheckInStatus && settings.filterByCheckInStatus !== 'all') {
        params.set('checkInStatus', settings.filterByCheckInStatus);
      }

      // Virtual periods - only tell server whether to return factor schedules
      // (actual virtual task generation happens client-side)
      if (settings?.includeVirtual !== undefined) {
        params.set('includeVirtual', settings.includeVirtual.toString());
      }

      // Note: lookaheadDays is used client-side only for virtual task generation

      // Sorting
      if (settings?.sortBy) {
        params.set('sortBy', settings.sortBy);
      }
      if (settings?.sortOrder) {
        params.set('sortOrder', settings.sortOrder);
      }

      // Context-mode: pass groupBySphere if the setting exists
      if (contextId && settings?.groupBySphere) {
        params.set('groupBySphere', 'true');
      }

      // Choose endpoint based on context mode
      return contextId
        ? `/tasks/context/${sphereId}/${encodeURIComponent(contextId)}?${params.toString()}`
        : `/tasks/${sphereId}?${params.toString()}`;
    };

    try {
      const { graphClient } = apiClients;
      const { pages, tasks: fetchedTasks, nextOffset, hasMore: more } = await readTaskPages(
        (pageOffset) => graphClient.get<ContextTasksResponse>(buildEndpoint(pageOffset), { signal: abortController.signal }),
        reset ? 0 : offset,
        reset && quiet ? offset : 0
      );

      // Bail out if this request was aborted (a newer request superseded it)
      if (abortController.signal.aborted) return;

      const first = pages[0];
      const last = pages.at(-1) ?? first;
      if (quiet) setError(null);

      if (reset) {
        setMaterializedTasks(fetchedTasks);
        // Store factor schedules for virtual task generation
        setFactorSchedules(first.factorSchedules || []);
      } else {
        setMaterializedTasks(prev => [...prev, ...fetchedTasks]);
      }

      setCounts(last.counts);
      setHasMore(more);
      setOffset(nextOffset);
      hasLoadedRef.current = true;

      // Context-mode metadata
      setContextMeta(last.contextMeta || null);
      setGroups(last.groups || null);
    } catch (err: unknown) {
      // A superseded read rejects however the client reports a cancel (axios `CanceledError`, a
      // DOM `AbortError`); its own signal is the one reliable sign, and its outcome is never applied.
      if (abortController.signal.aborted) return;

      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred while fetching tasks';
      if (quiet && isTransientReadFailure(err)) {
        console.error('[useTaskData] Background refresh failed; keeping the tasks on screen:', errorMessage);
        return;
      }
      setError(errorMessage);
      if (reset) {
        hasLoadedRef.current = false;
        setMaterializedTasks([]);
        setFactorSchedules([]);
        setContextMeta(null);
        setGroups(null);
        setOffset(0);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [apiClients, sphereId, contextId, settings, offset]);

  const refresh = useCallback(async () => {
    await fetchTasks(true);
  }, [fetchTasks]);

  const refetch = useCallback(() => {
    fetchTasks(true);
  }, [fetchTasks]);

  /** Re-reads the list after a sphere in it changed, without the loading state; a transient failure keeps the rows. */
  const refreshQuietly = useCallback(async () => {
    await fetchTasks(true, true);
  }, [fetchTasks]);

  const fetchMore = useCallback(async () => {
    if (!loading && hasMore) {
      await fetchTasks(false);
    }
  }, [fetchTasks, loading, hasMore]);

  // Initial fetch and dependency updates (including contextId changes)
  useEffect(() => {
    fetchTasks(true);
  }, [sphereId, contextId, settings?.filterByTaskType, settings?.filterByStatus, settings?.filterByCheckInStatus]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!settings?.refreshInterval || settings.refreshInterval <= 0) {
      return;
    }

    const intervalMs = settings.refreshInterval * 1000;

    refreshTimeoutRef.current = setInterval(() => {
      if (!loading) {
        fetchTasks(true);
      }
    }, intervalMs);

    return () => {
      if (refreshTimeoutRef.current) {
        clearInterval(refreshTimeoutRef.current);
      }
    };
  }, [settings?.refreshInterval, loading, fetchTasks]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearInterval(refreshTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Generate virtual tasks client-side from factor schedules and sort all tasks
  const tasks = useMemo(() => {
    const includeVirtual = settings?.includeVirtual !== false;
    const lookaheadDays = settings?.lookaheadDays || 90;

    let allTasks: Task[];

    if (!includeVirtual || factorSchedules.length === 0) {
      // No virtual tasks - just use materialized tasks
      allTasks = [...materializedTasks];
    } else {
      // Build set of existing task due dates per factor to avoid duplicates
      const existingTaskDueDates = new Map<string, Set<string>>();
      // Track factors with collapsed groups to prevent virtual re-expansion
      const collapsedFactorIds = new Set<string>();

      for (const task of materializedTasks) {
        if (task.taskType === 'check_in' && task.factorId) {
          if (task.groupCount && task.groupCount > 1) {
            collapsedFactorIds.add(task.factorId);
          }
          if (task.dueAt) {
            if (!existingTaskDueDates.has(task.factorId)) {
              existingTaskDueDates.set(task.factorId, new Set());
            }
            existingTaskDueDates.get(task.factorId)!.add(task.dueAt);
          }
        }
      }

      // Generate virtual tasks, suppressing collapsed factors explicitly
      const virtualTasks = generateVirtualTasksFromSchedules(
        factorSchedules,
        existingTaskDueDates,
        lookaheadDays,
        collapsedFactorIds
      );

      // Merge materialized and virtual tasks
      allTasks = [...materializedTasks, ...virtualTasks as Task[]];
    }

    // Always sort tasks by the selected field
    const sortBy = settings?.sortBy || 'dueAt';
    const sortOrder = settings?.sortOrder || 'asc';
    const sortDirection = sortOrder === 'asc' ? 1 : -1;

    allTasks.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'dueAt': {
          const aDate = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
          const bDate = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
          comparison = aDate - bDate;
          break;
        }
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'urgency':
          comparison = (a.urgency || 0) - (b.urgency || 0);
          break;
        default:
          break;
      }
      return comparison * sortDirection;
    });

    return allTasks;
  }, [materializedTasks, factorSchedules, settings?.includeVirtual, settings?.lookaheadDays, settings?.sortBy, settings?.sortOrder]);

  return {
    tasks,
    loading,
    error,
    counts,
    hasMore,
    refetch,
    refresh,
    refreshQuietly,
    fetchMore,
    contextMeta,
    groups,
  };
};

/**
 * Get stage display name with optional short form
 */
export const getStageDisplayName = (stage: string, isCompact = false): string => {
  const stageMap: Record<string, { full: string; short: string }> = {
    todo: { full: 'To Do', short: 'TD' },
    in_progress: { full: 'In Progress', short: 'IP' },
    complete: { full: 'Complete', short: 'C' },
    pending: { full: 'Pending', short: 'P' },
    overdue: { full: 'Overdue', short: 'OD' }
  };

  const stageInfo = stageMap[stage];
  if (!stageInfo) return stage;

  return isCompact ? stageInfo.short : stageInfo.full;
};

/**
 * Get status display name for unified task status
 */
export const getStatusDisplayName = (status: string): string => {
  const statusMap: Record<string, string> = {
    pending: 'Pending',
    in_progress: 'In Progress',
    complete: 'Complete',
    overdue: 'Overdue'
  };
  return statusMap[status] || status;
};

/**
 * Determine if a task stage can be transitioned (workflow tasks only)
 */
export const canTransitionStage = (currentStage?: string): boolean => {
  return currentStage !== undefined && currentStage !== 'complete';
};

/**
 * Get next possible stages for a workflow task
 */
export const getNextStages = (currentStage?: string): string[] => {
  switch (currentStage) {
    case 'todo':
      return ['in_progress'];
    case 'in_progress':
      return ['complete'];
    case 'complete':
      return []; // Terminal state
    default:
      return [];
  }
};

/**
 * Get task type display name
 */
export const getTaskTypeDisplayName = (taskType: string): string => {
  const typeMap: Record<string, string> = {
    workflow: 'Workflow Task',
    check_in: 'Check-in'
  };
  return typeMap[taskType] || taskType;
};

/**
 * Check if task is overdue
 */
export const isTaskOverdue = (task: Task): boolean => {
  if (task.status === 'overdue') return true;
  if (task.status === 'complete') return false;

  if (!task.dueAt) return false;

  return new Date(task.dueAt) < new Date();
};
